import React, { useEffect, useState } from 'react';
import type { ToolUseBlock } from '../../store/chatStore';
import { useChatStore } from '../../store/chatStore';
import { useToolResultContent } from './ToolResultDataContext';
import { langColor, langFromPath } from './codeBlockUtils';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

function basename(p: string) {
  return p.split('/').filter(Boolean).pop() ?? p;
}

function pathScope(p: string): 'skill' | 'session' | 'current' {
  if (p?.startsWith('skill://')) return 'skill';
  if (p?.startsWith('session://')) return 'session';
  return 'current';
}

const scopeBadge = (
  t: TFunction<'pleri/chat'>,
): Record<string, { label: string; textColor: string }> => ({
  skill: { label: t('skillBadge'), textColor: 'text-amber-400' },
  session: { label: t('sessionBadge'), textColor: 'text-blue-400' },
  current: { label: t('sessionBadge'), textColor: 'text-gray-500' },
});

function PathChip({ path, side }: { path: string; side: 'from' | 'to' }) {
  const { t } = useTranslation('pleri/chat');
  const scope = pathScope(path);
  const badge = scopeBadge(t)[scope];
  const name = basename(path);
  const isDir = path.endsWith('/');
  const color = isDir ? '#6b7280' : langColor(langFromPath(name));

  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className={`text-xs font-semibold uppercase tracking-widest ${badge.textColor}`}>
        {side === 'from' ? t('from') : t('to')}{' '}·{' '}{badge.label}
      </span>
      <div className="flex items-center gap-1.5">
        {!isDir && (
          <span
            className="h-1.5 w-1.5 flex-shrink-0 rounded-full"
            style={{ backgroundColor: color }}
          />
        )}
        {isDir && (
          <svg className="h-3 w-3 flex-shrink-0 text-gray-500" viewBox="0 0 16 16" fill="currentColor">
            <path d="M1.5 3A1.5 1.5 0 000 4.5v8A1.5 1.5 0 001.5 14h13a1.5 1.5 0 001.5-1.5v-7A1.5 1.5 0 0014.5 5h-6.5L6.5 3h-5z"/>
          </svg>
        )}
        <span className="truncate font-mono text-xs text-gray-200" title={path}>
          {path.length > 40 ? `…${path.slice(-38)}` : path}
        </span>
      </div>
    </div>
  );
}

export const CopyFileBlock: React.FC<{ content: ToolUseBlock }> = ({ content }) => {
  const activeConversationId = useChatStore((s) => s.activeConversationId);
  const isPending = useChatStore((s) =>
    activeConversationId
      ? (s.conversations[activeConversationId]?.pendingToolUses ?? new Set()).has(content.id)
      : false,
  );
  const resultJson = useToolResultContent(content.id);
  const isRunning = isPending || !resultJson;

  const from = typeof content.input?.from === 'string' ? content.input.from : '…';
  const to   = typeof content.input?.to   === 'string' ? content.input.to   : '…';

  // No auto-collapse — keep the from/to layout visible so the user can see what was copied

  // Parse result
  let resultText = '';
  let countLabel = '';
  let isError = false;
  if (resultJson) {
    resultText = (() => { try { return JSON.parse(resultJson) as string; } catch { return resultJson; } })();
    if (typeof resultText !== 'string') resultText = resultJson;
    const m = resultText.match(/Copied (\d+) file/);
    if (m) countLabel = `${m[1]} file${parseInt(m[1]) > 1 ? 's' : ''}`;
    // Error: result has content but no "Copied" match, or contains error keywords
    isError = !isRunning && !resultText.startsWith('Copied') && resultText.length > 0;
  }

  return (
    <div className={`overflow-hidden rounded-xl border bg-gray-950 shadow-lg ${isError ? 'border-red-900/50' : 'border-gray-800'}`}>
      <div className="flex items-center gap-3 px-3 py-2.5">
        {/* From */}
        <div className="min-w-0 flex-1">
          <PathChip path={from} side="from" />
        </div>

        {/* Arrow / error indicator */}
        <div className="flex flex-shrink-0 flex-col items-center gap-0.5">
          {isRunning ? (
            <svg className="h-4 w-4 animate-pulse text-gray-600" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          ) : isError ? (
            <svg className="h-4 w-4 text-red-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="h-4 w-4 text-emerald-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          )}
          {countLabel && (
            <span className="text-xs text-gray-600 font-medium">{countLabel}</span>
          )}
        </div>

        {/* To */}
        <div className="min-w-0 flex-1">
          <PathChip path={to} side="to" />
        </div>

        {/* Status */}
        <div className="flex-shrink-0 ml-1">
          {isRunning ? (
            <svg className="h-3 w-3 animate-spin text-yellow-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : (
            <div className={`h-2 w-2 rounded-full ${isError ? 'bg-red-500' : 'bg-emerald-500'}`} />
          )}
        </div>
      </div>

      {/* Error message */}
      {isError && resultText && (
        <div className="border-t border-red-900/30 px-3 py-2">
          <span className="text-xs text-red-400">{resultText}</span>
        </div>
      )}
    </div>
  );
};
